import { Stack, IconButton, Paper, Box, Button } from '@mui/material';
import { AddRounded, EditRounded } from '@mui/icons-material';
import { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CustomerDevice } from '@/core/entities';
import { Roles } from '@/core/types';
import { PAGINATION_META_DEFAULT } from '@/shared/constants/pagination';
import { PaginationMeta } from '@/shared/types/pagination';
import DeviceNameUpdateForm from '@/features/devices/DeviceNameUpdateForm';
import DeviceForm from '@/features/devices/DeviceForm';
import { useSiteDevices } from '@/queries/sites';
import Backdrop from '@/components/ui/backdrop';
import DataGrid, {
	Column,
	FilterRow,
	HeaderFilter,
	Item,
	Paging,
	SearchPanel,
	Sorting,
	Toolbar,
} from 'devextreme-react/data-grid';
import dayjs from 'dayjs';
import { DATE_FORMAT, TIME_FORMAT } from '@/shared/constants/date';
import MaintenanceDateCell from '@/features/devices/cells/MaintenanceDateCell';
import useAuth from '@/shared/context/auth-context';

interface SiteDevicesTableProps {
	siteId?: string;
	siteName?: string;
	customerName?: string;
}

const SiteDevicesTable: FunctionComponent<SiteDevicesTableProps> = ({
	siteId,
	siteName,
	customerName,
}): ReactElement => {
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const canManageDevices = user?.role === Roles.ACCOUNT_ADMIN;

	const [devices, setDevices] = useState<CustomerDevice[]>([]);
	const [, setPaginationMeta] = useState<PaginationMeta>(PAGINATION_META_DEFAULT);

	const {
		queryKey: devicesQueryKey,
		query: { isFetching, data: devicePages },
	} = useSiteDevices(siteId, { limit: 99999 });

	useEffect(() => {
		const devicePage = devicePages?.pages[0];
		if (devicePage) {
			const { page: currentPage, totalItems, totalPages, itemsPerPage: pageSize } = devicePage;
			setDevices(devicePage.data);
			setPaginationMeta({ totalItems, totalPages, currentPage, pageSize });
		}
	}, [devicePages]);

	// Device form
	const [deviceForm, setDeviceForm] = useState<{
		open: boolean;
		model?: CustomerDevice;
	}>({
		open: false,
		model: undefined,
	});

	const refetch = () => {
		/* eslint-disable @typescript-eslint/no-floating-promises */
		queryClient.invalidateQueries(devicesQueryKey);
	};

	const handleEdit = (device: CustomerDevice) => {
		setDeviceForm({ model: device, open: true });
	};

	const renderActionsCell = (cellData: { data: CustomerDevice }) => {
		const device = cellData.data;
		return (
			<Stack direction="row" spacing={1}>
				<IconButton
					size="small"
					color="primary"
					onClick={(e) => {
						e.stopPropagation();
						handleEdit(device);
					}}
				>
					<EditRounded fontSize="small" />
				</IconButton>
			</Stack>
		);
	};

	const renderLastSyncDateCell = (cellData: { data: CustomerDevice }) => {
		const { lastSyncDate } = cellData.data;
		const date = lastSyncDate && dayjs(lastSyncDate);
		if (!date) return null;
		return (
			<>
				{date.format(DATE_FORMAT)}
				<br />
				{date.format(TIME_FORMAT)}
			</>
		);
	};

	return (
		<Stack direction="column" spacing={2.5}>
			{isFetching ? (
				<Backdrop />
			) : (
				<Box component={Paper} p={3}>
					<DataGrid
						dataSource={devices}
						showBorders={true}
						columnAutoWidth={true}
						rowAlternationEnabled={true}
						allowColumnResizing={true}
						allowColumnReordering={true}
					>
						<Sorting mode="multiple" />
						<FilterRow visible={true} />
						<HeaderFilter visible={true} />
						<SearchPanel visible={true} highlightCaseSensitive={true} />
						<Paging defaultPageSize={50} enabled={true} />
						<Toolbar>
							{canManageDevices && (
								<Item location="before">
									<Button
										variant="contained"
										startIcon={<AddRounded />}
										onClick={() => setDeviceForm({ open: true, model: undefined })}
									>
										Create device
									</Button>
								</Item>
							)}
							<Item name="searchPanel" location="after" />
						</Toolbar>

						<Column dataField="name" caption="Device Name" />
						<Column dataField="serialNumber" caption="Serial No." />
						<Column
							dataField="maintenanceDate"
							caption="Maintenance Date"
							alignment="right"
							cellRender={({ data }: { data: CustomerDevice }) => (
								<MaintenanceDateCell maintenanceDate={data.maintenanceDate} />
							)}
						/>
						<Column
							dataField="lastSyncDate"
							caption="Last Sync Date"
							alignment="right"
							cellRender={renderLastSyncDateCell}
						/>
						<Column
							caption="Actions"
							width={80}
							allowFiltering={false}
							allowSorting={false}
							allowReordering={false}
							cellRender={renderActionsCell}
						/>
					</DataGrid>
				</Box>
			)}
			{canManageDevices ? (
				<DeviceForm
					open={deviceForm.open}
					onClose={() => setDeviceForm({ open: false, model: undefined })}
					device={deviceForm.model}
					onSubmitted={refetch}
					siteOptions={siteId ? [{ value: siteId, label: siteName ?? 'Current site' }] : []}
					customerName={customerName}
					deviceOptions={[
						{ value: 'space-1', label: 'Space-1' },
						{ value: 'nova', label: 'Nova' },
					]}
				/>
			) : (
				deviceForm.model && (
					<DeviceNameUpdateForm
						open={deviceForm.open}
						onClose={() => setDeviceForm({ open: false, model: undefined })}
						device={deviceForm.model}
						onSubmitted={refetch}
						customerName={customerName}
					/>
				)
			)}
		</Stack>
	);
};

export default SiteDevicesTable;
